import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn
} from 'material-ui/Table';
import Dialog from 'material-ui/Dialog';
import LinearProgress from 'material-ui/LinearProgress';
import SovosPaginationFooter from 's1-ui/sovos-pagination-footer';
import SovosFlatButton from 's1-ui/sovos-flat-button/SovosFlatButton';
import { gridHeaderColorGray } from 'theme/mainColors';
import { COST_OBJ_PROP_TYPE } from 'shared/constants/PropTypes';
import ContextualGridMenuOptions from '../../contextualGridMenuOptions/ContextualGridMenuOptions';
import './costObjsGrid.scss';

const headerLabelStyle = {
  color: gridHeaderColorGray,
  fontWeight: 400,
  fontSize: 15,
  paddingBottom: 0
};

class CostObjsGrid extends React.Component {
  constructor(props) {
    super(props);
    this.costObjsGridColumns = [
      {
        title: 'Name',
        dataKey: 'name',
        width: 150
      },
      {
        title: 'Description',
        dataKey: 'description',
        width: 270
      },
      {
        title: '',
        width: 40,
        dataKey: 'moreOptions',
        disabled: !props.showMoreActionsIcon
      }
    ];
    this.state = {
      hoveredRow: -1,
      itemsPerPage: 10,
      page: 0,
      deleteModalOpen: false,
      costObjIdToDelete: -1
    };
  }

  handleRowHover = hoveredRow => this.setState({ hoveredRow });

  handleCellClick = rowID =>
    this.props.onCostObjSelect(this.props.costObjsListGridData[rowID]);

  getStartItem = () =>
    this.props.totalItems ? this.state.itemsPerPage * this.state.page + 1 : 0;

  calculateEndItem = () => {
    const maximumItemsOnPage = this.state.itemsPerPage * (this.state.page + 1);
    if (maximumItemsOnPage > this.props.totalItems) {
      return this.props.totalItems;
    }
    return maximumItemsOnPage;
  };

  handlePrevPage = ev => {
    ev.preventDefault();
    this.setState(oldState => {
      if (oldState.page > 0) {
        this.props.onPageChange(oldState.page - 1, oldState.itemsPerPage);
        return {
          page: oldState.page - 1
        };
      } else {
        return {};
      }
    });
  };

  handleNextPage = ev => {
    ev.preventDefault();
    this.setState(oldState => {
      if ((oldState.page + 1) * oldState.itemsPerPage < this.props.totalItems) {
        this.props.onPageChange(oldState.page + 1, oldState.itemsPerPage);
        return {
          page: oldState.page + 1
        };
      } else {
        return {};
      }
    });
  };

  handlePerPageChanged = ev => {
    const pageSize = ev.target.value;
    this.setState({ page: 0, itemsPerPage: pageSize });
    this.props.onPageChange(0, pageSize);
  };

  handleEditCostObj = costObjId => this.props.onEditCostObj(costObjId);

  handleDeleteCostObjClick = costObjIdToDelete =>
    this.setState({ deleteModalOpen: true, costObjIdToDelete });

  handleCloseDeleteCostObj = () =>
    this.setState({ deleteModalOpen: false, costObjIdToDelete: -1 });

  handleDeleteCostObj = () => {
    this.props.onDeleteCostObj(this.state.costObjIdToDelete);
    this.handleCloseDeleteCostObj();
  };

  render() {
    const {
      costObjsListGridData,
      totalItems,
      fetchingCostObjs,
      showPagination,
      displayCheckbox,
      backgroundColor,
      onCloseDeleteFailed,
      errorMessage
    } = this.props;
    return (
      <div className="cost-objs-grid__main-container">
        {fetchingCostObjs && (
          <div className="cost-objs-grid__over-layer">
            <LinearProgress mode="indeterminate" />
          </div>
        )}
        <div
          className={ `cost-objs-grid__table-container${
            fetchingCostObjs ? ' blur-content' : ''
          }` }
        >
          {costObjsListGridData.length > 0 && (
            <Table
              key="CostObjsGrid"
              fixedHeader={ true }
              onRowHover={ this.handleRowHover }
              onRowHoverExit={ this.handleRowHoverExit }
              onCellClick={ this.handleCellClick }
              multiSelectable={ true }
              className="cost-objs-grid"
              style={ { backgroundColor: backgroundColor } }
            >
              <TableHeader
                displaySelectAll={ false }
                enableSelectAll={ false }
                adjustForCheckbox={ displayCheckbox }
                style={ { border: 'none' } }
              >
                <TableRow style={ { border: 'none' } }>
                  {this.costObjsGridColumns.map(
                    ({ dataKey, width, title, disabled }) =>
                      !disabled && (
                        <TableHeaderColumn
                          key={ dataKey }
                          style={ {
                            width: width || 'auto',
                            ...headerLabelStyle
                          } }
                        >
                          {title}
                        </TableHeaderColumn>
                      )
                  )}
                </TableRow>
              </TableHeader>
              <TableBody
                showRowHover={ true }
                displayRowCheckbox={ displayCheckbox }
                deselectOnClickaway={ false }
              >
                {costObjsListGridData.map((costObj, index) => (
                  <TableRow
                    id={ `import-cost-obj-grid-row-${index}` }
                    key={ index }
                    hovered={ costObj.disabled }
                    hoverable={ !costObj.disabled }
                    selectable={ !costObj.disabled }
                    selected={ costObj.selected }
                    className={ `cost-objs-grid__table-row${
                      costObj.disabled
                        ? ' cost-objs-grid__table-row_not-selectable'
                        : ''
                    }` }
                  >
                    {this.costObjsGridColumns.map(
                      ({ dataKey, width, disabled }) =>
                        !disabled && (
                          <TableRowColumn
                            key={ dataKey }
                            style={ {
                              width: width || 'auto',
                              textOverflow: `${
                                dataKey !== 'moreOptions' ? 'ellipsis' : 'unset'
                              }`
                            } }
                          >
                            {dataKey !== 'moreOptions'
                              ? costObj[dataKey]
                              : this.state.hoveredRow === index && (
                                <ContextualGridMenuOptions
                                  elementId={ costObj.id }
                                  onEdit={ this.handleEditCostObj }
                                  onDelete={ this.handleDeleteCostObjClick }
                                  editDisabled={ false }
                                  deleteDisabled={ false }
                                />
                              )}
                          </TableRowColumn>
                        )
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {costObjsListGridData.length > 0 && showPagination && (
            <SovosPaginationFooter
              key="CostObjsPaginationFooter"
              endItem={ this.calculateEndItem() }
              itemsPerPage={ this.state.itemsPerPage }
              startItem={ this.getStartItem() }
              totalItems={ totalItems }
              onPrevClicked={ this.handlePrevPage }
              onNextClicked={ this.handleNextPage }
              onPerPageChanged={ this.handlePerPageChanged }
              itemsPerPageOptions={ [10, 25, 50] }
            />
          )}
          <Dialog
            actions={ [
              <SovosFlatButton
                key="close-delete"
                color="secondary"
                onClick={ this.handleCloseDeleteCostObj }
              >
                No
              </SovosFlatButton>,
              <SovosFlatButton
                key="submit-delete"
                onClick={ this.handleDeleteCostObj }
              >
                Yes
              </SovosFlatButton>
            ] }
            modal={ true }
            open={ this.state.deleteModalOpen }
          >
            Are you sure you want to delete this cost object?
          </Dialog>
          <Dialog
            actions={ [
              <SovosFlatButton key="close-delete" onClick={ onCloseDeleteFailed }>
                Close
              </SovosFlatButton>
            ] }
            modal={ true }
            open={ errorMessage !== '' }
            onRequestClose={ onCloseDeleteFailed }
          >
            {errorMessage}
          </Dialog>
        </div>
      </div>
    );
  }
}

CostObjsGrid.propTypes = {
  totalItems: PropTypes.number,
  costObjsListGridData: PropTypes.arrayOf(COST_OBJ_PROP_TYPE).isRequired,
  onCostObjSelect: PropTypes.func,
  onDeleteCostObj: PropTypes.func,
  onEditCostObj: PropTypes.func,
  onPageChange: PropTypes.func,
  onCloseDeleteFailed: PropTypes.func,
  fetchingCostObjs: PropTypes.bool,
  showPagination: PropTypes.bool,
  displayCheckbox: PropTypes.bool,
  showRemoveIcon: PropTypes.bool,
  showMoreActionsIcon: PropTypes.bool,
  backgroundColor: PropTypes.string,
  errorMessage: PropTypes.bool
};

CostObjsGrid.defaultProps = {
  fetchingCostObjs: false,
  onCostObjSelect: () => null,
  totalItems: 0,
  onPageChange: () => null,
  showPagination: false,
  displayCheckbox: false,
  showRemoveIcon: false,
  showMoreActionsIcon: false,
  backgroundColor: '#fff'
};

export default CostObjsGrid;



// WEBPACK FOOTER //
// ./src/shared/components/costObjsGrid/CostObjsGrid.js